import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo, useState, useEffect } from 'react'
import { PencilSquare } from 'react-bootstrap-icons'
import { useParams } from 'react-router-dom'
import { InfluencerContext } from '../../../../../contexts/InfluencerContext'
import { SharedCampaignContext } from '../../../../../contexts/Advertiser/SharedCampaignContext'
import { SharedInfluencerContext } from '../../../../../contexts/Advertiser/SharedInfluencerContext'
import Authentication from '../../../../../models/Authentication'
import DescriptionModal from './DescriptionModal'
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import CountryModal from './CountryModal'
import axiosService from '../../../../../utils/AxiosService'
import { toastError, toastSuccess } from '../../../../../components/FormComponents/Toasts'

export default observer(function BioDescription() {
  const [modalOpen, setModalOpen] = useState(false)
  const [countryModalOpen, setCountryModalOpen] = useState(false)
  const params = useParams()
  const { id, authentication_id, campaign_authentication_id } = params
  const influencerContext = useContext(InfluencerContext)
  const sharedCampaignContext = useContext(SharedCampaignContext)
  const sharedInfluencerContext = useContext(SharedInfluencerContext)
  const [authentications, setAuthentications] = useState([])

  const isAdvertiserContext = !!sharedCampaignContext?.campaignState
  const isInfluencerShareContext = !!sharedInfluencerContext?.influencerState
  const currentId = isInfluencerShareContext
    ? (sharedInfluencerContext?.authentications?.[0]?.id)
    : (isAdvertiserContext ? authentication_id : id)

  useEffect(() => {
    if (isAdvertiserContext && authentication_id) {
      axiosService.get(`/api/advertiser/campaign_share/authentications/${authentication_id}/authentications`)
        .then(authentications => {
          const mappedAuthentications = authentications.map(authentication => new Authentication(authentication))
          setAuthentications(mappedAuthentications)
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error?.data?.error || error?.error || error?.message || 'An error occurred'
          toastError(errorMessage)
        })
    }
  }, [isAdvertiserContext, authentication_id])

  const pmAuthentications = influencerContext?.authentications || []

  const resetToModashCountry = authentication => {
    axiosService.patch(`/api/project_manager/authentications/${authentication.id}/use_modash_country`)
      .then(({ country }) => {
        const accounts = pmAuthentications.length ? pmAuthentications : [authentication]
        accounts.forEach(account => {
          account.country = country
          account.country_locked = false
        })
        toastSuccess('Country reset to the Modash value')
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  const sharedInfluencerAuthentications = sharedInfluencerContext?.authentications || []
  const currentAuthentications = isInfluencerShareContext
    ? sharedInfluencerAuthentications
    : (isAdvertiserContext ? authentications : pmAuthentications)

  const currentAuthentication = useMemo(() => currentAuthentications.find(authentication => +authentication.id === +currentId ) || new Authentication(), [currentId, currentAuthentications, currentAuthentications.length] )

  return (
    <div className="w-full bg-white dark:bg-rb-dark-mode p-4 rounded-md">
      <p className="font-bold text-xl mb-4 dark:text-white">Bio and description</p>
      <div className="grid grid-cols-3 gap-4">
        <div className="border-1 dark:border-rb-dark-mode-lighter rounded-lg p-4 h-full flex items-center">
          <p className="dark:text-white">{currentAuthentication.bio}</p>
        </div>
        <div className="border-1 dark:border-rb-dark-mode-lighter rounded-lg p-4 col-span-2">
          <div className="flex mb-4 items-start justify-between">
            <p className="text-sm dark:text-white">{currentAuthentication.description || 'No Description.'}</p>
            {!isAdvertiserContext && !isInfluencerShareContext && <button onClick={() => setModalOpen(true)} className="flex items-center text-sm font-bold ml-4 dark:text-white"><PencilSquare /> Edit</button>}
          </div>
          <div className="flex flex-wrap gap-2">
            {currentAuthentication.authentications_hashtags?.map(({ hashtag: { name } }, index) => (
              <p key={`${name}${index}`} className="bg-gray-100 dark:bg-rb-dark-mode-lighter dark:text-white rounded-md font-semibold text-xs px-2">#{name}</p>
            ))}
          </div>
        </div>
        <div className="border-1 dark:border-rb-dark-mode-lighter rounded-lg p-4 h-full flex items-center justify-between">
          <div>
            <p className="font-bold dark:text-white">Country of origin</p>
            <div className="flex items-center">
              {currentAuthentication.country && getUnicodeFlagIcon(currentAuthentication.country)}
              <p className="ml-1 dark:text-white">{currentAuthentication.country}</p>
            </div>
            {!isAdvertiserContext && !isInfluencerShareContext && currentAuthentication.country_locked && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Set manually
                {currentAuthentication.modash_country && currentAuthentication.modash_country !== currentAuthentication.country && (
                  <>
                    {' · Modash reports '}{getUnicodeFlagIcon(currentAuthentication.modash_country)} {currentAuthentication.modash_country}
                    <button type="button" onClick={() => resetToModashCountry(currentAuthentication)} className="ml-2 font-semibold text-rb-green hover:underline">Use this</button>
                  </>
                )}
              </p>
            )}
          </div>
          {!isAdvertiserContext && !isInfluencerShareContext && <button onClick={() => setCountryModalOpen(true)} className="flex items-center text-sm font-bold ml-4 dark:text-white"><PencilSquare /> Edit</button>}
        </div>
      </div>
      {!isAdvertiserContext && !isInfluencerShareContext && modalOpen && <DescriptionModal authentication={currentAuthentication} setIsOpen={setModalOpen} isOpen={modalOpen} />}
      {!isAdvertiserContext && !isInfluencerShareContext && countryModalOpen && <CountryModal authentication={currentAuthentication} setIsOpen={setCountryModalOpen} isOpen={countryModalOpen} />}
    </div>
  )
})